import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getMeetingsForMyOrg } from "api";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import useGoalsQuery from "hooks/useGoalsQuery";
import { useUser } from "stores/useUser";
import { combineMeetingLists } from "utils/insightsMerge";
import MeetingDistModal from "../MeetingDistBarCard/MeetingDistModal";

// Every insights card drills into the same popup, so it is mounted once by the
// layout and driven entirely by the URL. Cards only say what to open.
//
// Combined My Time cards send several keys, because cost and distribution have
// no "all" bucket in redis — their meetings live under the organizer key and
// the attendee key, and the popup shows both.
const InsightsDrilldownHost = () => {
  const { drilldown, closeDrilldown } = useInsightsDrilldown();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { data: goals } = useGoalsQuery(user?.subscription?.organization_id);

  const redisKeys = useMemo(() => {
    if (Array.isArray(drilldown?.redisKeys)) {
      return drilldown.redisKeys.filter(Boolean);
    }
    return drilldown?.redisKey ? [drilldown.redisKey] : [];
  }, [drilldown]);

  const {
    data: meetingDetails,
    isLoading,
    isError,
    error,
  } = useQuery(
    ["insightsDrilldown", redisKeys],
    async () => {
      const lists = await Promise.all(
        redisKeys.map((key) => getMeetingsForMyOrg(key)),
      );
      return combineMeetingLists(...lists);
    },
    {
      enabled: redisKeys.length > 0,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  );

  const visibleMeetings = useMemo(() => {
    if (!drilldown?.ratedOnly) return meetingDetails;
    // Match the rating eligibility used by the distribution calculation.
    return meetingDetails?.filter((meeting) =>
      meeting.ratings?.some(
        (rating) => Number.isFinite(rating) && rating >= 0 && rating <= 5,
      ),
    );
  }, [meetingDetails, drilldown?.ratedOnly]);

  // Set once a save has asked the background job to rebuild this user's
  // charts. The pie is refetched when the popup closes, by which time the
  // rebuild has normally finished.
  const rebuildRequested = useRef(false);

  const refetchInsightsIfRebuilt = useCallback(() => {
    if (!rebuildRequested.current) return;
    rebuildRequested.current = false;
    queryClient.invalidateQueries("userInsights");
    queryClient.invalidateQueries("MyOrgInsights");
  }, [queryClient]);

  useEffect(() => refetchInsightsIfRebuilt, [refetchInsightsIfRebuilt]);

  // The rows are a Redis snapshot that still holds the old goal until the
  // rebuild has run, so patch the cache rather than refetching it.
  const handleGoalsSaved = useCallback(
    (savedByDocId, recalc) => {
      queryClient.setQueryData(["insightsDrilldown", redisKeys], (rows) =>
        (rows || []).map((row) => {
          const saved = savedByDocId[row.recurringEventId || row.id];
          if (!saved) return row;
          return {
            ...row,
            strategicGoal: {
              goal: saved.goal,
              color: saved.color,
              category: saved.category,
              categoryId: saved.categoryId,
              source: "user",
            },
          };
        }),
      );
      if (recalc?.accepted) rebuildRequested.current = true;
    },
    [queryClient, redisKeys],
  );

  const handleClose = useCallback(() => {
    refetchInsightsIfRebuilt();
    closeDrilldown();
  }, [refetchInsightsIfRebuilt, closeDrilldown]);

  if (!drilldown) return null;

  return (
    <MeetingDistModal
      opened
      onClose={handleClose}
      data={drilldown}
      meetingDetails={redisKeys.length ? visibleMeetings : []}
      isLoadingMeetings={redisKeys.length > 0 && isLoading}
      isErrorMeetings={isError}
      meetingError={error}
      goals={goals}
      onGoalsSaved={handleGoalsSaved}
    />
  );
};

export default InsightsDrilldownHost;
